import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
} from '@angular/core';

import type { User } from '@recruitee/account-types';
import { AnalyticsMode, NotificationsAnalytics } from '@recruitee/analytics';
import { I18nService } from '@recruitee/i18n';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  GroupComponent,
  IconComponent,
  OverlayOriginDirective,
  RtStatefulComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { TotalFollowersComponent } from '../total-followers/total-followers.component';

type State = {
  title: string;
  description: string;
  dropdownOpen: boolean;
};

@Component({
  selector: 'rt-follow-button',
  templateUrl: './follow-button.component.html',
  styleUrls: ['./follow-button.component.less'],
  providers: [NotificationsAnalytics],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    OverlayOriginDirective,
    IconComponent,
    TotalFollowersComponent,
    RtTooltip,
  ],
})
export class FollowButtonComponent extends RtStatefulComponent<State> {
  @Input() public type: string;
  @Input() public followed: boolean;
  @Input() public kind: string;
  @Input() public loading: boolean = false;
  @Input() public responsive: boolean = false;
  @Input() public users: User[] = [];

  public followClick = output<void>();
  public unfollowClick = output<void>();

  @ViewChild(RtTooltip)
  public tooltip?: RtTooltip;

  public tooltipText: string | null;

  public statusText: string;

  constructor(
    private i18n: I18nService,
    private analytics: NotificationsAnalytics,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({
      title: '',
      description: '',
      dropdownOpen: false,
    });
  }

  public ngOnInit(): void {
    if (this.type === 'candidate' || this.type === 'requisition') {
      this.setState({
        ...this.state,
        title: this.i18n.translate(`app.follow_dropdown.${this.type}_title`),
        description: this.i18n.translate(`app.follow_dropdown.${this.type}_description`),
      });
    } else {
      this.setState({
        ...this.state,
        title: this.i18n.translate(`app.follow_dropdown.${this.type}_title_${this.kind}`),
        description: this.i18n.translate(
          `app.follow_dropdown.${this.type}_description_${this.kind}`,
        ),
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading || changes.followed) {
      if (this.state.dropdownOpen) {
        this.setState(
          {
            ...this.state,
            dropdownOpen: false,
          },
          false,
        );
      }
    }

    if (this.type === 'candidate' || this.type === 'requisition') {
      this.tooltipText = this.followed
        ? this.i18n.translate(`app.follow_dropdown.unfollow_btn_tooltip_${this.type}`)
        : this.i18n.translate(`app.follow_dropdown.follow_btn_tooltip_${this.type}`);
    } else if (this.type === 'offer') {
      this.tooltipText = this.followed
        ? this.i18n.translate(
            this.kind === 'talent_pool'
              ? 'app.follow_dropdown.unfollow_btn_tooltip_talent_pool'
              : 'app.follow_dropdown.unfollow_btn_tooltip_job',
          )
        : this.i18n.translate(
            this.kind === 'talent_pool'
              ? 'app.follow_dropdown.follow_btn_tooltip_talent_pool'
              : 'app.follow_dropdown.follow_btn_tooltip_job',
          );
    }

    this.setStatusText();
  }

  public followAction(): void {
    this.followClick.emit();
    this.analytics.trackFollowAction(
      this.type === 'offer' ? this.kind : this.type,
      AnalyticsMode.Single,
    );
  }

  public unfollowAction(): void {
    this.unfollowClick.emit();
    this.analytics.trackUnfollowAction(
      this.type === 'offer' ? this.kind : this.type,
      AnalyticsMode.Single,
    );
  }

  public toggleDropdown(state: boolean | undefined = undefined): void {
    this.setState({
      ...this.state,
      dropdownOpen: state === undefined ? !this.state.dropdownOpen : state,
    });
  }

  public trackOpenSettings(): void {
    this.analytics.trackOpenSettings();
  }

  public hideTooltip(tooltip?: RtTooltip): void {
    tooltip?.hideSubject.next({
      ...tooltip?.hideSubject.value,
      eventEmitted: true,
    });
  }

  public showTooltip(tooltip?: RtTooltip): void {
    tooltip?.showSubject?.next({ delay: 0 });
  }

  private setStatusText(): void {
    this.statusText = this.followed
      ? this.i18n.translate('app.follow_dropdown.following')
      : this.i18n.translate('app.follow_dropdown.follow');
  }
}
